import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Empty, Spin } from "antd";
import { fetchActivePackages } from "../api/subscriptionPackagesApi";
import { featureTexts, formatMoney, periodLabel } from "../utils/pricing";
import { toastError } from "../utils/toast";
import OnDemandSection from "../components/OnDemandSection";
import PlansExtras from "../components/PlansExtras";
import "./css/pricing.css";

export default function PlansPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const subscriptionType = String(type || "personal").toLowerCase();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchActivePackages({ type: subscriptionType });
        if (!on) return;
        const list = Array.isArray(data) ? data : [];
        setPackages(list);
        const hasMonthly = list.some((pkg) => pkg.billing_cycle === "monthly");
        const hasAnnual = list.some((pkg) => pkg.billing_cycle === "annual");
        if (!hasMonthly && hasAnnual) setCycle("annual");
        else if (hasMonthly) setCycle("monthly");
      } catch {
        toastError("Failed to load services.");
        if (on) setPackages([]);
      } finally {
        if (on) setLoading(false);
      }
    })();
    return () => {
      on = false;
    };
  }, [subscriptionType]);

  const visible = useMemo(
    () => packages.filter((pkg) => pkg.billing_cycle === cycle),
    [packages, cycle]
  );

  const hasMonthly = packages.some((pkg) => pkg.billing_cycle === "monthly");
  const hasAnnual = packages.some((pkg) => pkg.billing_cycle === "annual");

  const handleStart = (pkg) => {
    if (pkg.cta_type === "contact") {
      navigate("/contact");
      return;
    }
    navigate(`/subscribe/details?package_id=${pkg.id}`);
  };

  return (
    <>
    <section className="cl-plans">
      <div className="cl-container">
        <p className="cl-eyebrow">Services</p>
        <h1 className="cl-plans-title">Compare Service Packages</h1>
        <p className="cl-plans-sub">
          {subscriptionType === "business"
            ? "Give your company a defined legal allowance. Compare Click Law services, switch billing cycle, and continue to checkout."
            : "Explore and compare our available services, choose the option that suits your needs, proceed to checkout, or submit a request for a tailored solution."}
        </p>

        {(hasMonthly || hasAnnual) && (
          <div className="cl-cycle-toggle" role="tablist" aria-label="Billing cycle">
            {hasMonthly && (
              <button
                type="button"
                className={cycle === "monthly" ? "active" : ""}
                onClick={() => setCycle("monthly")}
              >
                Monthly
              </button>
            )}
            {hasAnnual && (
              <button
                type="button"
                className={cycle === "annual" ? "active" : ""}
                onClick={() => setCycle("annual")}
              >
                Annual
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="cl-pricing-loading">
            <Spin />
          </div>
        ) : visible.length === 0 ? (
          <Empty description="No active services for this billing cycle yet." />
        ) : (
          <div className={`cl-plan-grid count-${Math.min(visible.length, 4)}`}>
            {visible.map((pkg) => (
              <article
                key={pkg.id}
                className={`cl-plan-card ${Number(pkg.is_featured) === 1 ? "featured" : ""}`}
              >
                {Number(pkg.is_featured) === 1 && <span className="cl-plan-badge">Most popular</span>}
                <h3>{pkg.name}</h3>
                {pkg.cta_type === "contact" ? (
                  <div className="cl-plan-price">
                    <strong>Let&apos;s talk</strong>
                    <span>Tailored pricing</span>
                  </div>
                ) : (
                  <div className="cl-plan-price">
                    <strong>{formatMoney(pkg.price, pkg.currency)}</strong>
                    <span>{periodLabel(pkg)}</span>
                  </div>
                )}
                {pkg.description && <p className="cl-plan-desc">{pkg.description}</p>}
                <button type="button" className="btn btn-solid cl-plan-cta" onClick={() => handleStart(pkg)}>
                  {pkg.cta_type === "contact" ? "Contact Us" : "Get Started"}
                </button>
                <ul className="cl-plan-features">
                  {featureTexts(pkg).map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        )}

       
      </div>
    </section>
    {subscriptionType === "personal" ? <OnDemandSection /> : null}
    <PlansExtras
      type={subscriptionType === "business" ? "business" : "personal"}
      showOnDemand={subscriptionType !== "personal"}
    />
    </>
  );
}
